import React from "react";
import {
  SIM_DATA,
  SIM_THRESHOLD_DEFAULT,
  SIM_THRESHOLD_MAX,
  SIM_THRESHOLD_MIN,
  SIM_THRESHOLD_STEP,
  autoCount,
  isQuotable,
  sliderPct,
} from "@/lib/simulator";

// Per-row chip/dot styling, ported verbatim from the design's renderVals().
const chipBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  padding: "5px 12px",
  borderRadius: "var(--radius-status)",
  fontSize: "var(--text-caption)",
  fontWeight: 600,
  whiteSpace: "normal",
  border: "1px solid transparent",
  flex: "none",
  transition: "background var(--motion-fast) ease-out, color var(--motion-fast) ease-out",
};

function chipStyle(ok: boolean): React.CSSProperties {
  return {
    ...chipBase,
    background: ok ? "var(--info-surface)" : "var(--warning-surface)",
    color: ok ? "var(--info-ink)" : "var(--warning-ink)",
  };
}

function dotStyle(ok: boolean): React.CSSProperties {
  return {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    flex: "none",
    background: ok ? "var(--info-ink)" : "var(--warning-ink)",
    transition: "background .35s ease",
  };
}

/** The interactive "turn a rule" card: threshold slider + six sample requests. */
export function RuleSimulator() {
  const [threshold, setThreshold] = React.useState(SIM_THRESHOLD_DEFAULT);
  const pct = sliderPct(threshold);
  const quotable = autoCount(SIM_DATA, threshold);

  return (
    <div style={{ background: "var(--surface)", border: "1px solid rgba(var(--ink-rgb),.11)", borderRadius: "16px", boxShadow: "0 16px 44px -24px rgba(var(--ink-rgb),.22)", padding: "clamp(22px,4vw,36px)" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "clamp(26px,4vw,52px)" }}>
        <div style={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: "18px" }}>
          <h3 style={{ margin: 0, fontSize: "clamp(20px,2.4vw,24px)", fontWeight: 600, letterSpacing: "-.01em" }}>Drehen Sie an einer Regel.</h3>
          <p style={{ margin: 0, fontSize: "15px", lineHeight: 1.64, color: "var(--muted)", textWrap: "pretty" }}>Verschieben Sie die Schwelle — und sehen Sie sofort, wie sich die Beispiel-Anfragen entscheiden. Genau so deterministisch arbeitet das Regelwerk im Produkt.</p>
          <div style={{ background: "var(--surface-quiet)", borderRadius: "12px", padding: "18px 20px", display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ margin: 0, fontSize: "clamp(16px,1.8vw,18px)", fontWeight: 500, lineHeight: 1.5, color: "var(--ink)" }}>„Ab <span style={{ fontWeight: 700, color: "var(--accent)", fontVariantNumeric: "tabular-nums" }}>{threshold}</span> Gästen geht die Anfrage zur manuellen Prüfung."</p>
            <input
              type="range"
              min={SIM_THRESHOLD_MIN}
              max={SIM_THRESHOLD_MAX}
              step={SIM_THRESHOLD_STEP}
              value={threshold}
              onChange={(event) => setThreshold(+event.target.value)}
              aria-label="Prüf-Schwelle in Gästen"
              style={{ WebkitAppearance: "none", appearance: "none", width: "100%", height: "6px", borderRadius: "99px", outline: "none", cursor: "pointer", border: "none", background: `linear-gradient(to right, var(--accent) ${pct}%, var(--track) ${pct}%)` }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontWeight: 600, color: "var(--faint)" }}><span>20 Gäste</span><span>120 Gäste</span></div>
          </div>
          <p style={{ margin: 0, fontSize: "14.5px", lineHeight: 1.6, color: "var(--text)" }}><strong style={{ fontWeight: 600, color: "var(--accent)", fontVariantNumeric: "tabular-nums" }}>{quotable} von 6</strong> Beispiel-Anfragen wären sofort angebotsfähig.</p>
          <p style={{ margin: 0, fontSize: "12.5px", color: "var(--faint)" }}>Fiktive Beispiel-Anfragen. Im Produkt simulieren Sie gegen Ihre echte Historie.</p>
        </div>
        <div style={{ flex: "1.2 1 380px", minWidth: 0, display: "flex", flexDirection: "column" }}>
          {SIM_DATA.map((row) => {
            const ok = isQuotable(row.g, threshold);
            return (
              <div key={row.ev} style={{ display: "flex", alignItems: "center", gap: "14px", padding: "13px 4px", borderBottom: "1px solid rgba(var(--ink-rgb),.08)" }}>
                <span style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: "1px" }}>
                  <span style={{ fontSize: "14.5px", fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.ev}</span>
                  <span style={{ fontSize: "12.5px", color: "var(--muted-2)" }}>{row.type}</span>
                </span>
                <span style={{ fontSize: "13.5px", fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flex: "none" }}>{row.g} Gäste</span>
                <span style={chipStyle(ok)}><span style={dotStyle(ok)}></span>{ok ? "Angebot möglich" : "Prüfung nötig"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
